<script lang="ts" setup>
import type { FormKitNode } from '@formkit/core'
import type { FieldData } from '~~/server/api/forms/form'
import { fileSize } from '~~/utils/file-size'

export interface FormKitCustomFileProps {
    data: FieldData
}

export type FileInputValue = {
    file: File
    name: string
}

const props = defineProps<FormKitCustomFileProps>()

const { $t } = useNuxtApp()

const oneMBInByte: number = 1048576
const defaultMaxFileSizeInMB: number = 12

const validations = computed(() => {
    if (!props.data.validations) return ''
    return props.data.validations
        .split('|')
        .filter((rule: string) => !rule.startsWith('file_mimes'))
        .join('|')
})

const maxFileSize = computed<number>(() => {
    const maxSizeRule = validations.value.split('|').find((rule: string) => rule.startsWith('file_max:'))
    if (!maxSizeRule) return defaultMaxFileSizeInMB
    return parseInt(maxSizeRule.replace('file_max:', '')) * oneMBInByte
})

// validate max file size
const size = (node: FormKitNode): boolean => {
    const value = node.value as Array<FileInputValue>
    const file = value[0]?.file
    if (!file) return true

    const totalSize = value.reduce((sum, item) => sum + (item?.file?.size ?? 0), 0)
    return totalSize <= maxFileSize.value
}

const enabledFileExtensions = [
    '.jpeg',
    '.jpg',
    '.png',
    '.gif',
    '.svg',
    '.webp',
    '.heic',
    '.ppt',
    '.pptx',
    '.pdf',
    '.doc',
    '.docx',
    '.xls',
    '.xlsx',
]

const fileExtensions = computed(() => {
    return (
        props.data.validations
            ?.split('|')
            .find((rule: string) => rule.startsWith('file_mimes'))
            ?.replace('file_mimes:', '') || enabledFileExtensions.join(',')
    )
})

const accept = (node: FormKitNode): boolean => {
    const value = node.value as Array<FileInputValue>
    const file = value[0]?.file
    if (!file) return true

    const fileName = file.name
    const extension = fileName.split('.').pop() || ''
    return fileExtensions.value.includes(`.${extension}`)
}

const validationMessages = computed(() => {
    return {
        accept: `${$t('Povolené typy súborov sú')} ${fileExtensions.value}`,
        size: `${props.data.multiple ? $t('Maximálna povolená veľkost všetkých pridaných súborov je spolu') : $t('Maximálna povolená veľkosť súboru je')} ${maxFileSize.value ? fileSize(maxFileSize.value, { unit: 'MB' }) : `${defaultMaxFileSizeInMB} ${$t('MB')}`}`,
        required: props.data.validation_messages?.['required'] || $t('Príloha je povinná.'),
    }
})

</script>

<template>
  <FormKit
    :label="props.data.label"
    :help="props.data.help"
    :name="props.data.name"
    :accept="fileExtensions"
    :placeholder="props.data.placeholder"
    :validation="`${validations}|size|accept`"
    :validation-label="props.data.label"
    :delay="600"
    :validation-rules="{ size, accept }"
    :floating-label="false"
    :validation-messages="validationMessages"
    type="file"
    :multiple="props.data.multiple"
  />
</template>
